import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { resetPassword } from '../services/users';
import LanguageSwitcher from '../components/languageSwitcher';
import './auth.css';

// Mirrors PasswordSchema in the API: length only, no composition rules.
const PASSWORD_MIN_LENGTH = 15
const PASSWORD_MAX_LENGTH = 128

const ResetPasswordPage = () => {

    const { t } = useTranslation();
    const { token } = useParams()
    const navigate = useNavigate()

    const [password, setPassword] = useState('')
    const [confirm, setConfirm] = useState('')
    const [error, setError] = useState('')
    const [isSubmitting, setIsSubmitting] = useState(false)
    // status: 'form' | 'success'
    const [status, setStatus] = useState('form')
    const [doneMessage, setDoneMessage] = useState('')

    useEffect(() => {
        if (status !== 'success') return
        const id = setTimeout(() => navigate('/login', { replace: true }), 3000)
        return () => clearTimeout(id)
    }, [status, navigate])

    const handleSubmit = async (e) => {
        e.preventDefault()
        setError('')

        if (!token) {
            setError(t('auth.resetLinkMissing'))
            return
        }
        if (password.length < PASSWORD_MIN_LENGTH) {
            setError(t('auth.passwordMinLength', { min: PASSWORD_MIN_LENGTH }))
            return
        }
        if (password.length > PASSWORD_MAX_LENGTH) {
            setError(t('auth.passwordMaxLength', { max: PASSWORD_MAX_LENGTH }))
            return
        }
        if (password !== confirm) {
            setError(t('auth.passwordsDoNotMatch'))
            return
        }

        setIsSubmitting(true)
        try {
            const body = await resetPassword(token, password)
            if (body && body.code === 200) {
                // Names the account ("Password updated for <username>…"): one person can own several.
                setDoneMessage(typeof body.message === 'string' ? body.message : '')
                setStatus('success')
            } else {
                setError(typeof body?.message === 'string'
                    ? body.message
                    : t('auth.passwordResetFallbackError'))
            }
        } catch (err) {
            setError(t('auth.somethingWentWrong'))
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <div className="auth-page">
            <div className="auth-shell">

                {/* ───────── LEFT / BRAND PANEL ───────── */}
                <aside className="auth-brand-panel">
                    <div className="auth-brand-top">
                        <Link to="/login" className="auth-brand-link" aria-label={t('auth.logoGoToSignIn')}>
                            <img
                                src={process.env.PUBLIC_URL + '/logo-soroby.svg'}
                                alt="Soroby"
                                className="auth-brand-logo"
                            />
                        </Link>
                    </div>

                    <div className="auth-brand-message">
                        <h1 className="auth-brand-title" dangerouslySetInnerHTML={{ __html: t('auth.brandTitle') }} />
                        <p className="auth-brand-tagline">
                            {t('auth.brandTagline')}
                        </p>
                    </div>

                    <div className="auth-brand-footer">
                        <span>© {new Date().getFullYear()} Soroby · {t('auth.builtForServiceBusinesses')}</span>
                        <span>soroby.com</span>
                    </div>
                </aside>

                {/* ───────── RIGHT / FORM OR SUCCESS PANEL ───────── */}
                <main className="auth-form-panel">
                    <LanguageSwitcher className="auth-lang" id="auth-language-drop" drop="down" align="end" showIcon />

                    <div className="auth-form-inner">

                        <div className="auth-mobile-brand">
                            <Link to="/login" className="auth-brand-link" aria-label={t('auth.logoGoToSignIn')}>
                                <img src={process.env.PUBLIC_URL + '/logo-soroby.svg'} alt="Soroby" />
                            </Link>
                        </div>

                        {status === 'form' && (
                            <>
                                <div className="auth-form-heading">
                                    <h2>{t('auth.resetPasswordTitle')}</h2>
                                    <p>{t('auth.resetSubtitle', { min: PASSWORD_MIN_LENGTH })}</p>
                                </div>

                                <form className="auth-form" onSubmit={handleSubmit}>
                                    <div className="auth-field">
                                        <label className="auth-label" htmlFor="reset-password">{t('auth.newPassword')}</label>
                                        <div className="auth-input-wrap">
                                            <svg className="auth-input-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                                                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                                            </svg>
                                            <input
                                                id="reset-password"
                                                className="auth-input"
                                                type="password"
                                                autoComplete="new-password"
                                                placeholder={t('auth.atLeastNCharacters', { min: PASSWORD_MIN_LENGTH })}
                                                required
                                                value={password}
                                                disabled={isSubmitting}
                                                onChange={(e) => setPassword(e.target.value)}
                                            />
                                        </div>
                                        {password.length > 0 && password.length < PASSWORD_MIN_LENGTH && (
                                            <div className="auth-secondary" style={{ marginTop: 6, textAlign: 'left' }}>
                                                {t('auth.moreCharactersToGo', { count: PASSWORD_MIN_LENGTH - password.length })}
                                            </div>
                                        )}
                                    </div>

                                    <div className="auth-field">
                                        <label className="auth-label" htmlFor="reset-confirm">{t('auth.confirmNewPassword')}</label>
                                        <div className="auth-input-wrap">
                                            <svg className="auth-input-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                                                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                                            </svg>
                                            <input
                                                id="reset-confirm"
                                                className="auth-input"
                                                type="password"
                                                autoComplete="new-password"
                                                placeholder={t('auth.repeatPassword')}
                                                required
                                                value={confirm}
                                                disabled={isSubmitting}
                                                onChange={(e) => setConfirm(e.target.value)}
                                            />
                                        </div>
                                    </div>

                                    <button
                                        type="submit"
                                        className="auth-btn"
                                        disabled={!password || !confirm || isSubmitting}
                                    >
                                        {isSubmitting ? (
                                            <span>{t('auth.updating')}</span>
                                        ) : (
                                            <>
                                                <span>{t('auth.updatePassword')}</span>
                                                <span className="arrow" aria-hidden="true">→</span>
                                            </>
                                        )}
                                    </button>

                                    {error && (
                                        <div className="auth-error">{error}</div>
                                    )}
                                </form>

                                <p className="auth-secondary">
                                    <Link to="/login">{t('auth.backToSignIn')}</Link>
                                </p>
                            </>
                        )}

                        {status === 'success' && (
                            <>
                                <div className="auth-success-icon auth-success-icon--ok" aria-hidden="true">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M20 6L9 17l-5-5" />
                                    </svg>
                                </div>

                                <div className="auth-form-heading">
                                    <h2>{t('auth.passwordUpdated')}</h2>
                                    <p>{doneMessage || t('auth.canNowSignIn')}</p>
                                </div>

                                <Link to="/login" className="auth-btn">
                                    <span>{t('auth.goToSignIn')}</span>
                                    <span className="arrow" aria-hidden="true">→</span>
                                </Link>

                                <p className="auth-secondary">{t('auth.redirecting')}</p>
                            </>
                        )}

                    </div>
                </main>

            </div>
        </div>
    )
}

export default ResetPasswordPage;
